/**
 * Componente de Ranking de Operadores
 * Exibe operadores ordenados por horas trabalhadas com medalhas e barras de progresso
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Trophy, 
  Clock, 
  ClipboardList, 
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react';
import { authHeaders } from '../utils/api';
import { fetchJsonResilient } from '../utils/resilientFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Medalhas para top 3
const getMedal = (position) => {
  switch (position) {
    case 1: return '🥇';
    case 2: return '🥈';
    case 3: return '🥉';
    default: return null;
  }
};

// Cores para as barras de progresso baseadas na posição
const getProgressColor = (position) => {
  switch (position) {
    case 1: return 'bg-gradient-to-r from-yellow-500 to-yellow-400';
    case 2: return 'bg-gradient-to-r from-gray-400 to-gray-300';
    case 3: return 'bg-gradient-to-r from-amber-700 to-amber-600';
    default: return 'bg-gradient-to-r from-blue-600 to-blue-500';
  }
};

export default function OperatorRanking({ compact = false }) {
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({ total_operators: 0, total_hours_all: 0 });

  useEffect(() => {
    fetchRanking();
  }, [period]);

  const fetchRanking = async () => {
    try {
      const params = new URLSearchParams({
        date_from: period.date_from,
        date_to: period.date_to
      });

      const data = await fetchJsonResilient(
        `${API_URL}/api/dashboard/operator-ranking?${params}`,
        { headers: authHeaders() }
      );

      if (data) {
        setRanking(data.ranking || []);
        setStats({
          total_operators: data.total_operators,
          total_hours_all: data.total_hours_all
        });
      } else {
        toast.error('Erro ao carregar ranking');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  // Atalhos de período
  const setPeriodPreset = (preset) => {
    const today = new Date();
    let from, to;

    switch (preset) {
      case 'week':
        from = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        to = today;
        break;
      case 'month':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
        break;
      case 'quarter':
        from = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        to = today;
        break;
      case 'year':
        from = new Date(today.getFullYear(), 0, 1);
        to = today;
        break;
      default:
        return;
    }

    setPeriod({
      date_from: from.toISOString().split('T')[0],
      date_to: to.toISOString().split('T')[0]
    });
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Versão compacta para sidebar ou resumo
  if (compact) {
    return (
      <div className="bg-[#111111] border border-gray-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={20} className="text-yellow-500" />
          <h3 className="text-white font-semibold">Top Operadores</h3>
        </div>
        
        <div className="space-y-3">
          {ranking.slice(0, 5).map((op) => (
            <div key={op.id} className="flex items-center gap-3">
              <span className="text-lg w-6">{getMedal(op.position) || op.position}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{op.name}</p>
                <p className="text-yellow-500 text-xs font-mono">{op.total_hours}h</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-gray-800">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-500/10 p-2.5 rounded-lg">
              <Trophy size={24} className="text-yellow-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Ranking de Operadores</h2>
              <p className="text-gray-400 text-sm">
                {formatDate(period.date_from)} a {formatDate(period.date_to)}
              </p>
            </div>
          </div>

          {/* Botão de filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm transition-colors"
          >
            <Filter size={16} />
            Filtrar Período
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setPeriodPreset('week')}
                className="px-3 py-1.5 bg-gray-800 hover:bg-yellow-500/20 hover:text-yellow-500 rounded-lg text-sm text-gray-300 transition-colors"
              >
                Última Semana
              </button>
              <button
                onClick={() => setPeriodPreset('month')}
                className="px-3 py-1.5 bg-gray-800 hover:bg-yellow-500/20 hover:text-yellow-500 rounded-lg text-sm text-gray-300 transition-colors"
              >
                Este Mês
              </button>
              <button
                onClick={() => setPeriodPreset('quarter')}
                className="px-3 py-1.5 bg-gray-800 hover:bg-yellow-500/20 hover:text-yellow-500 rounded-lg text-sm text-gray-300 transition-colors"
              >
                Este Trimestre
              </button>
              <button
                onClick={() => setPeriodPreset('year')}
                className="px-3 py-1.5 bg-gray-800 hover:bg-yellow-500/20 hover:text-yellow-500 rounded-lg text-sm text-gray-300 transition-colors"
              >
                Este Ano
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">De:</label>
                <input
                  type="date"
                  value={period.date_from}
                  onChange={(e) => setPeriod({ ...period, date_from: e.target.value })}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Até:</label>
                <input
                  type="date"
                  value={period.date_to}
                  onChange={(e) => setPeriod({ ...period, date_to: e.target.value })}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats resumo */}
      <div className="grid grid-cols-2 gap-4 p-4 lg:p-6 bg-gray-900/30">
        <div className="text-center">
          <p className="text-gray-400 text-sm">Total de Operadores</p>
          <p className="text-2xl font-bold text-white">{stats.total_operators}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-400 text-sm">Total de Horas</p>
          <p className="text-2xl font-bold text-yellow-500">{stats.total_hours_all}h</p>
        </div>
      </div>

      {/* Lista de operadores */}
      <div className="p-4 lg:p-6">
        {ranking.length === 0 ? (
          <div className="text-center py-8">
            <User size={48} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum operador com horas no período</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ranking.map((op) => (
              <div
                key={op.id}
                className={`
                  bg-[#0A0A0A] border rounded-xl p-4 transition-all
                  ${op.position <= 3 
                    ? 'border-yellow-500/30 hover:border-yellow-500/50' 
                    : 'border-gray-800 hover:border-gray-700'
                  }
                `}
              >
                <div className="flex items-center gap-4">
                  {/* Posição / Medalha */}
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                    {getMedal(op.position) ? (
                      <span className="text-3xl">{getMedal(op.position)}</span>
                    ) : (
                      <span className="text-2xl font-bold text-gray-500">#{op.position}</span>
                    )}
                  </div>

                  {/* Foto do operador */}
                  <div className="flex-shrink-0">
                    {op.photo ? (
                      <img
                        src={op.photo}
                        alt={op.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-gray-700"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center border-2 border-gray-700">
                        <User size={24} className="text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Info do operador */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold truncate">{op.name}</h3>
                      {op.position === 1 && (
                        <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-full">
                          Líder
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Clock size={14} className="text-yellow-500" />
                        {op.total_hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <ClipboardList size={14} className="text-blue-400" />
                        {op.total_orders} O.S.
                      </span>
                    </div>

                    {/* Barra de progresso */}
                    <div className="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${getProgressColor(op.position)}`}
                        style={{ width: `${op.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{op.percentage}% do líder</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
